import * as XLSX from 'xlsx';

/** Fila de exportación: la clave es el encabezado de la columna. */
export type ExportRow = Record<string, string | number | boolean | null | undefined>;

/** Excel no acepta más de 31 caracteres en el nombre de la hoja. */
const MAX_SHEET_NAME = 31;

/** Caracteres no válidos en un nombre de archivo en Windows. */
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;

/** Deja un texto usable como parte del nombre del archivo. */
export const safeFileNamePart = (value: string | undefined | null, fallback = 'Elecnor'): string => {
  const clean = (value || '').replace(INVALID_FILENAME_CHARS, '-').trim();
  return clean || fallback;
};

/** Fecha de hoy como YYYY-MM-DD, para versionar el archivo exportado. */
export const todayStamp = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Una hoja del libro. */
export interface ExportSheet {
  name: string;
  rows: ExportRow[];
}

/** Convierte las filas en una hoja, con el ancho de columna ajustado al contenido. */
const buildWorksheet = (rows: ExportRow[]): XLSX.WorkSheet => {
  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Ancho por columna según el contenido más largo, para que no se vea "####".
  const headers = Object.keys(rows[0]);
  worksheet['!cols'] = headers.map((header) => {
    const longestValue = rows.reduce((max, row) => {
      const length = row[header] === null || row[header] === undefined ? 0 : String(row[header]).length;
      return Math.max(max, length);
    }, header.length);
    return { wch: Math.min(longestValue + 2, 60) };
  });

  return worksheet;
};

/**
 * Descarga varias hojas en un solo .xlsx. Las hojas vacías se omiten.
 *
 * @returns false si ninguna hoja traía filas (quien llama muestra el aviso).
 */
export const exportSheetsToExcel = (sheets: ExportSheet[], fileName: string): boolean => {
  const withRows = sheets.filter(sheet => sheet.rows.length > 0);
  if (withRows.length === 0) return false;

  const workbook = XLSX.utils.book_new();
  withRows.forEach(sheet => {
    XLSX.utils.book_append_sheet(workbook, buildWorksheet(sheet.rows), sheet.name.slice(0, MAX_SHEET_NAME));
  });

  XLSX.writeFile(workbook, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  return true;
};

/**
 * Descarga las filas como .xlsx de una sola hoja.
 *
 * Las claves del primer objeto definen los encabezados, así que todas las filas
 * deben traer las mismas claves y en el mismo orden.
 *
 * @returns false si no había nada que exportar (quien llama muestra el aviso).
 */
export const exportRowsToExcel = (rows: ExportRow[], sheetName: string, fileName: string): boolean =>
  exportSheetsToExcel([{ name: sheetName, rows }], fileName);
